<template>
  <nav class="bottom-nav" ref="navContainer">
    <div class="active-pill" :style="pillStyle"></div>
    <router-link 
      to="/home" 
      class="nav-item" 
      :class="{ active: $route.path === '/home' || $route.path === '/' }"
    >
      <div class="icon-wrapper">
        <img :src="homeIcon" alt="Home" class="icon" />
      </div>
      <span class="label">HOME</span>
    </router-link>
    <router-link 
      to="/news" 
      class="nav-item" 
      :class="{ active: $route.path.startsWith('/news') }"
    >
      <div class="icon-wrapper">
        <img :src="newsIcon" alt="News" class="icon" />
      </div>
      <span class="label">NEWS</span>
    </router-link>
    <router-link 
      to="/live-tv" 
      class="nav-item nav-item-livetv" 
      :class="{ active: $route.path === '/live-tv' }"
    >
      <div class="icon-wrapper">
        <img :src="liveTvIcon" alt="Live TV" class="icon" />
        <div class="coming-soon-badge">
          <span>COMING SOON</span>
          <div class="badge-pointer"></div>
        </div>
      </div>
      <span class="label">LIVE TV</span>
    </router-link>
    <router-link 
      to="/fixtures" 
      class="nav-item" 
      :class="{ active: $route.path === '/fixtures' }"
    >
      <div class="icon-wrapper">
        <img :src="fixturesIcon" alt="Fixtures" class="icon" />
      </div>
      <span class="label">FIXTURES</span>
    </router-link>
    <router-link 
      to="/profile" 
      class="nav-item" 
      :class="{ active: $route.path === '/profile' }"
    >
      <div class="icon-wrapper">
        <img :src="profileIcon" alt="Profile" class="icon" />
      </div>
      <span class="label">PROFILE</span>
    </router-link>
  </nav>
</template>

<script>
import homeIcon from '@/assets/icons/home-icon.png'
import newsIcon from '@/assets/icons/news-icon.png'
import liveTvIcon from '@/assets/icons/live-tv-icon.png'
import fixturesIcon from '@/assets/icons/fixtures-icon.png'
import profileIcon from '@/assets/icons/profile-icon.png'

export default {
  name: 'BottomNav',
  data() {
    return {
      homeIcon,
      newsIcon,
      liveTvIcon,
      fixturesIcon,
      profileIcon,
      pillStyle: {
        transform: 'translateX(0)',
        width: '0px',
        opacity: 0
      }
    }
  },
  watch: {
    '$route'() {
      this.updatePill()
    }
  },
  mounted() {
    this.updatePill()
    window.addEventListener('resize', this.updatePill)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updatePill)
  },
  methods: {
    updatePill() {
      this.$nextTick(() => {
        const activeItem = this.$el?.querySelector('.nav-item.active')
        if (activeItem && this.$refs.navContainer) {
          const navRect = this.$refs.navContainer.getBoundingClientRect()
          const itemRect = activeItem.getBoundingClientRect()
          const offsetX = itemRect.left - navRect.left
          const pillWidth = itemRect.width

          this.pillStyle = {
            transform: `translateX(${offsetX}px)`,
            width: `${pillWidth}px`,
            opacity: 1
          }
        } else {
          this.pillStyle = { transform: 'translateX(0)', width: '0px', opacity: 0 }
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/tokens';

$glass-bg: rgba(8, 8, 10, 0.94);
$glass-edge: rgba(255, 255, 255, 0.04);
$accent-glow: rgba($brand-red, 0.35);

.bottom-nav {
  position: fixed;
  bottom: $space-base;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - #{$space-2xl});
  max-width: calc(#{$app-max-width} - #{$space-2xl});
  background: $glass-bg;
  backdrop-filter: blur(32px) saturate(220%);
  -webkit-backdrop-filter: blur(32px) saturate(220%);
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 8px 6px 10px;
  z-index: 1000;
  border-radius: 20px;
  border: 1px solid $glass-edge;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.25) inset,
    0 12px 48px -12px rgba(0, 0, 0, 0.55),
    0 0 40px -8px rgba($brand-red, 0.06);
  overflow: visible;
}

.active-pill {
  position: absolute;
  top: 6px;
  left: 0;
  bottom: 6px;
  border-radius: 12px;
  background: linear-gradient(
    180deg,
    rgba($brand-red, 0.28) 0%,
    rgba($brand-red, 0.12) 40%,
    rgba($brand-red, 0.2) 100%
  );
  border: 1px solid rgba($brand-red, 0.5);
  pointer-events: none;
  z-index: 1;
  transition: transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1),
              width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1),
              opacity 0.4s ease;
  will-change: transform, width;
  box-shadow:
    0 0 24px -4px $accent-glow,
    0 4px 12px -4px rgba($brand-red, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);

  &::after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 2px;
    border-radius: 2px;
    background: $brand-red;
    box-shadow: 0 0 10px $brand-red;
  }
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  text-decoration: none;
  color: rgba($white, 0.35);
  font-size: 9px;
  font-weight: 600;
  padding: 5px 4px;
  position: relative;
  z-index: 2;
  transition: color 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-tap-highlight-color: transparent;

  &.active {
    color: $white;
  }

  &:hover:not(.active) {
    color: rgba($white, 0.8);
  }
}

.icon-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 10px;
  z-index: 3;
  transition: all 0.4s cubic-bezier(0.34, 1.2, 0.64, 1);
}

.nav-item:hover .icon-wrapper {
  background: rgba(255, 255, 255, 0.04);
}

.nav-item.active .icon-wrapper {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 0 20px -4px rgba($brand-red, 0.25);
}

.icon {
  width: 21px;
  height: 21px;
  object-fit: contain;
  opacity: 0.45;
  transition: opacity 0.4s ease,
              transform 0.45s cubic-bezier(0.34, 1.3, 0.64, 1),
              filter 0.4s ease;
  position: relative;
  z-index: 3;
}

.nav-item.active .icon {
  opacity: 1;
  transform: scale(1.1) translateY(-1px);
  filter: drop-shadow(0 0 8px rgba($brand-red, 0.4));
}

.nav-item:hover:not(.active) .icon {
  opacity: 0.8;
}

.nav-item-livetv {
  color: rgba(#FF6B9D, 0.45);

  &.active {
    color: #FF9EC4;
  }

  &:hover:not(.active) {
    color: rgba(#FF6B9D, 0.9);
  }
}

.nav-item-livetv .icon {
  opacity: 0.5;
}

.nav-item-livetv.active .icon {
  opacity: 1;
  filter: drop-shadow(0 0 8px rgba(255, 107, 157, 0.45));
}

.coming-soon-badge {
  position: absolute;
  top: -26px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, $brand-red 0%, #b8151a 100%);
  color: $white;
  font-size: 8px;
  font-weight: 800;
  padding: 5px 11px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  white-space: nowrap;
  line-height: 1.2;
  letter-spacing: 1px;
  box-shadow:
    0 4px 16px -2px rgba($brand-red, 0.5),
    0 0 0 1px rgba(0, 0, 0, 0.2) inset;
  z-index: 1001;
  pointer-events: none;
  animation: badgePulse 3s ease-in-out infinite;
}

.badge-pointer {
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #b8151a;
}

.label {
  font-size: 9px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  line-height: 1;
  position: relative;
  z-index: 3;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-item.active .label {
  font-weight: 700;
  letter-spacing: 0.8px;
}

@keyframes badgePulse {
  0%, 100% { transform: translateX(-50%) scale(1) translateY(0); }
  50% { transform: translateX(-50%) scale(1.04) translateY(-1px); }
}
</style>
